import React, { useState, useEffect } from "react";
import {
  Show,
  Datagrid,
  TabbedShowLayout,
  TextField,
  useTranslate,
  FunctionField,
  ArrayField,
  NumberField,
  Filter,
  TextInput,
  NumberInput,
  ReferenceOneField,
  useRecordContext,
} from "react-admin";
import {
  DateField,
  Title,
  ShowOnlyTopToolbar,
  EmptyMessage,
} from "../../../shared/components/index.js";
import moment from "moment-timezone";
import PropTypes from "prop-types";

const DatexTransactionFilter = (props) => {
  const { setSum, setAmount } = props;

  return (
    <Filter {...props}>
      <TextInput
        source="fn_card_owner"
        label="resources.Client.fields.mango_transactions.fn_card_owner"
        alwaysOn
        resettable={true}
      />
      <TextInput
        source="n_accounts_struc"
        label="resources.Client.fields.mango_transactions.n_accounts_struc"
        alwaysOn
        resettable={true}
      />
      <NumberInput
        source="amount"
        label="resources.Client.fields.mango_transactions.amount_ok"
        alwaysOn
        onChange={(e) => setAmount(e.target.value)}
        resettable="true"
      />
      <NumberInput
        source="sum"
        label="resources.Client.fields.mango_transactions.sum_ok"
        onChange={(e) => setSum(e.target.value)}
        alwaysOn
        resettable="true"
      />
    </Filter>
  );
};

DatexTransactionFilter.propTypes = {
  setSum: PropTypes.func,
  setAmount: PropTypes.func,
};

const FilteredTransactions = ({ data }) => {
  if (!data || data.length === 0) {
    return <EmptyMessage />;
  }

  return (
    <Datagrid
      empty={<EmptyMessage />}
      rowClick={false}
      bulkActionButtons={false}
      data={data}
      total={data.length}
    >
      <TextField
        source="fn_card_owner"
        label="resources.Client.fields.mango_transactions.fn_card_owner"
      />
      <NumberField
        source="amount_ok"
        label="resources.Client.fields.mango_transactions.amount_ok"
      />
      <NumberField
        source="sum_ok"
        label="resources.Client.fields.mango_transactions.sum_ok"
      />
      <TextField
        source="n_accounts_struc"
        label="resources.Client.fields.mango_transactions.n_accounts_struc"
      />
      <TextField
        source="n_service_station"
        label="resources.Client.fields.mango_transactions.n_service_station"
      />
      <TextField
        source="address"
        label="resources.Client.fields.mango_transactions.address"
      />
      <TextField
        source="n_issuers"
        label="resources.Client.fields.mango_transactions.n_issuers"
      />
      <NumberField
        source="confirm_status"
        label="resources.Client.fields.mango_transactions.confirm_status"
      />
      <FunctionField
        source="session_time"
        render={(
          record // 'record' here is the individual transaction object
        ) =>
          record &&
          moment
            .tz(record.session_time, "Europe/Kiev")
            .utcOffset(0)
            .format("DD.MM.YYYY HH:mm:ss")
        }
        label="resources.Client.fields.mango_transactions.session_time"
      />
    </Datagrid>
  );
};

FilteredTransactions.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const TabbedShowLayoutTabs = () => {
  const [sum, setSum] = useState(null);
  const [amount, setAmount] = useState(null);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const t = useTranslate();
  const record = useRecordContext();

  useEffect(() => {
    if (record && record.transactions) {
      let filtered = [...record.transactions];

      if (sum !== null && sum !== undefined && sum !== "") {
        const parsedSum = parseFloat(sum);

        if (!isNaN(parsedSum)) {
          filtered = filtered.filter(
            (transaction) => parseFloat(transaction.sum_ok) >= parsedSum
          );
        }
      }

      if (amount !== null && amount !== undefined && amount !== "") {
        const parsedAmount = parseFloat(amount);

        if (!isNaN(parsedAmount)) {
          filtered = filtered.filter(
            (transaction) => parseFloat(transaction.amount_ok) >= parsedAmount
          );
        }
      }

      setFilteredTransactions(filtered);
    } else {
      setFilteredTransactions([]);
    }
  }, [record, sum, amount]);

  return (
    <TabbedShowLayout>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.info">
        <TextField source="phone" />
        <TextField source="first_name" />
        <TextField source="last_name" />
        <TextField source="email" />
        <NumberField source="balance" />
        <FunctionField
          source="entity"
          render={(record) => {
            if (record.entity === 1) {
              return t("resources.Client.source.entity.physical");
            }

            if (record.entity === 2) {
              return t("resources.Client.source.entity.legal");
            }
          }}
        />
        <TextField source="city" />
        <TextField
          source="address"
          style={{
            display: "inline-block",
            maxWidth: "15em",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        />
        <FunctionField
          source="status"
          render={(record) =>
            t(`resources.Client.source.status.${record.status}`)
          }
        />
        <ArrayField
          source="unconfirmed_changes"
          label="resources.Client.fields.unconfirmed_changes.name"
        >
          <Datagrid optimized bulkActionButtons={null}>
            <TextField
              source="field_name"
              label="resources.Client.fields.unconfirmed_changes.field_name"
            />
            <TextField
              source="value"
              label="resources.Client.fields.unconfirmed_changes.value"
            />
            <FunctionField
              source="status"
              render={(record) =>
                t(`resources.Client.source.changes_status.${record.status}`)
              }
            />
          </Datagrid>
        </ArrayField>
        <DateField source="created_at" />
        <DateField source="updated_at" />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.cards">
        <ArrayField
          perPage={10}
          source="cards"
          label="resources.Client.source.infoTab.cards"
        >
          <Datagrid rowClick={false} bulkActionButtons={false}>
            <ReferenceOneField
              label="resources.Client.fields.mango_cards.card_owner"
              source="card_owner"
              reference="Client"
              target="id_clients"
            >
              <TextField source="first_name" />
            </ReferenceOneField>
            <TextField
              source="card_name"
              label="resources.Client.fields.mango_cards.card_name"
            />
            <TextField
              source="phone"
              label="resources.Client.fields.mango_cards.phone"
            />
            <NumberField source="balance" />
            <FunctionField
              source="is_archive"
              render={(record) =>
                record && record.is_archive === true
                  ? t("resources.QrCard.source.status.archive")
                  : t("resources.QrCard.source.status.active")
              }
              label="resources.QrCard.fields.is_archive"
            />
            <FunctionField
              source="begin_date"
              render={(record) =>
                record &&
                moment
                  .tz(record.begin_date, "Europe/Kiev")
                  .utcOffset(+2)
                  .format("DD.MM.YYYY")
              }
              label="resources.QrCard.fields.begin_date"
            />
            <FunctionField
              source="end_date"
              render={(record) =>
                record &&
                moment
                  .tz(record.end_date, "Europe/Kiev")
                  .utcOffset(+2)
                  .format("DD.MM.YYYY")
              }
              label="resources.QrCard.fields.end_date"
            />
          </Datagrid>
        </ArrayField>
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.transactions">
        <ArrayField
          perPage={record && record.transactions?.length}
          source="transactions"
          label="resources.Client.source.infoTab.transactions"
        >
          <>
            <DatexTransactionFilter setSum={setSum} setAmount={setAmount} />
            {sum || amount ? (
              <FilteredTransactions data={filteredTransactions} />
            ) : (
              <Datagrid
                empty={<EmptyMessage />}
                rowClick={false}
                bulkActionButtons={false}
              >
                <TextField
                  source="fn_card_owner"
                  label="resources.Client.fields.mango_transactions.fn_card_owner"
                />
                <NumberField
                  source="amount_ok"
                  label="resources.Client.fields.mango_transactions.amount_ok"
                />
                <NumberField
                  source="sum_ok"
                  label="resources.Client.fields.mango_transactions.sum_ok"
                />
                <TextField
                  source="n_accounts_struc"
                  label="resources.Client.fields.mango_transactions.n_accounts_struc"
                />
                <TextField
                  source="n_service_station"
                  label="resources.Client.fields.mango_transactions.n_service_station"
                />
                <TextField
                  source="address"
                  label="resources.Client.fields.mango_transactions.address"
                />
                <TextField
                  source="n_issuers"
                  label="resources.Client.fields.mango_transactions.n_issuers"
                />
                <NumberField
                  source="confirm_status"
                  label="resources.Client.fields.mango_transactions.confirm_status"
                />
                <FunctionField
                  source="session_time"
                  render={(transactionRecord) =>
                    transactionRecord &&
                    moment
                      .tz(transactionRecord.session_time, "Europe/Kiev")
                      .utcOffset(0)
                      .format("DD.MM.YYYY HH:mm:ss")
                  }
                  label="resources.Client.fields.mango_transactions.session_time"
                />
              </Datagrid>
            )}
          </>
        </ArrayField>
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.payment_transactions">
        <ArrayField
          perPage={record && record.payment_transactions?.length}
          source="payment_transactions"
          label="resources.Client.source.infoTab.payment_transactions"
        >
          <Datagrid rowClick={false} bulkActionButtons={false}>
            <TextField
              source="client_fn"
              label="resources.Client.fields.mango_payment_transactions.client_fn"
            />
            <TextField
              source="payment_note"
              label="resources.Client.fields.mango_payment_transactions.payment_note"
            />
            <FunctionField
              source="status"
              render={(record) => {
                if (record.status === 1) {
                  return t(
                    `resources.Client.source.payment_status.${record.status}`
                  );
                }
              }}
            />
            <NumberField
              source="s_docums"
              textAlign="center"
              label="resources.Client.fields.mango_payment_transactions.s_docums"
            />
            <FunctionField
              source="d_confirm"
              render={(record) =>
                record &&
                moment
                  .tz(record.d_confirm, "Europe/Kiev")
                  .utcOffset(0)
                  .format("DD.MM.YYYY HH:mm:ss")
              }
              label="resources.Client.fields.mango_payment_transactions.session_time"
            />
          </Datagrid>
        </ArrayField>
      </TabbedShowLayout.Tab>
    </TabbedShowLayout>
  );
};

const ShowClient = () => {
  return (
    <Show title={<Title source="phone" />} actions={<ShowOnlyTopToolbar />}>
      <TabbedShowLayout
        tabs={
          <TabbedShowLayoutTabs variant="scrollable" scrollButtons={false} />
        }
      />
    </Show>
  );
};

export default ShowClient;
